import * as THREE from 'three'
import { Controls } from 'three'
import { FIRST_PERSON_LOOK_SPEED, FIRST_PERSON_MOVEMENT_SPEED } from '@/shared/constants'
import { CameraMode } from '@/shared/types/core'
import {
  computeRollFreeUp,
  isCurrentViewportCamera,
  NATIVE_CONTROL_SURFACE_KINDS,
  type NativeControlServices,
  resolveNavigationScale,
  shouldUseAsyncPicking,
  syncViewportCameraFromRig,
} from './NativeControlUtils'

/** LegacyFirstPersonController 构造参数 */
export interface LegacyFirstPersonControllerOptions {
  /** 控制器共享服务集合 */
  services: NativeControlServices
}

const WORLD_UP = new THREE.Vector3(0, 0, 1)
const LEGACY_ROTATION_SPEED = 200
const DRAG_TRANSLATION_SCALE = 100
const DRAG_TRANSLATION_SPEED_MULTIPLIER = 4
const FADE_FACTOR = 50
const MIN_SPEED = 0.1
const DOUBLE_CLICK_FOCUS_RATIO = 0.2
const DOUBLE_CLICK_MIN_DISTANCE = 0.2
const DOUBLE_CLICK_DURATION_MS = 600
const MIN_PITCH = -Math.PI / 2
const MAX_PITCH = Math.PI / 2
const FIRST_PERSON_ACTIVATION_TRANSITION_MS = 180
const UP_ALIGNMENT_EPSILON = 1e-3
const MIN_KEYBOARD_MAX_SPEED = 1
const KEYBOARD_ACCELERATION_DURATION = 1

/**
 * Potree legacy-style first person controller:
 * - left drag rotates
 * - right drag pans in screen space
 * - wheel adjusts move speed
 * - WASD/QE move the camera
 */
export class LegacyFirstPersonController extends Controls<THREE.Camera> {
  /** 平移速度，单位米/秒 */
  movementSpeed = FIRST_PERSON_MOVEMENT_SPEED
  /** 视角旋转速度，单位弧度/秒 */
  lookSpeed = FIRST_PERSON_LOOK_SPEED
  /** 是否锁定高度轴移动 */
  lockElevation = false
  /** 是否禁用鼠标旋转 */
  disableRotation = false

  private services: NativeControlServices | null = null
  private readonly translationDelta = new THREE.Vector3()
  private readonly translationWorldDelta = new THREE.Vector3()
  private readonly keyState = new Set<string>()
  private keyboardMaxSpeed = MIN_KEYBOARD_MAX_SPEED
  private keyboardCurrentSpeed = 0
  private activePointerId: number | null = null
  private activeDragButton: number | null = null
  private lastPointerScreen: THREE.Vector2 | null = null
  private yawDelta = 0
  private pitchDelta = 0
  private activationToken = 0
  private isActivating = false
  private pickRequestVersion = 0
  private pickAbortController: AbortController | null = null

  private boundPointerDown: ((event: PointerEvent) => void) | null = null
  private boundPointerMove: ((event: PointerEvent) => void) | null = null
  private boundPointerUp: ((event: PointerEvent) => void) | null = null
  private boundWheel: ((event: WheelEvent) => void) | null = null
  private boundContextMenu: ((event: MouseEvent) => void) | null = null
  private boundKeyDown: ((event: KeyboardEvent) => void) | null = null
  private boundKeyUp: ((event: KeyboardEvent) => void) | null = null
  private boundDoubleClick: ((event: MouseEvent) => void) | null = null

  constructor(options: LegacyFirstPersonControllerOptions) {
    const camera = syncViewportCameraFromRig(options.services)
    super(camera, null)

    this.services = options.services
    this.enabled = false
  }

  /**
   * 连接 DOM 元素并启动激活过渡。
   *
   * @param element - 承载交互事件的 HTML 元素
   */
  override connect(element: HTMLElement): void {
    if (!this.services) {
      return
    }

    if (this.services.rig.mode !== CameraMode.Perspective) {
      const state = this.services.rig.getState()
      this.services.rig.setState({
        ...state,
        mode: CameraMode.Perspective,
      })
    }

    const camera = this.syncFromRig()
    super.connect(element)
    this.installEventHandlers(element)
    camera.lookAt(this.services.rig.target)
    camera.updateMatrixWorld(true)
    void this.beginActivationTransition()
  }

  /** 断开 DOM 连接，移除事件监听并停止运动累积 */
  override disconnect(): void {
    this.activationToken += 1
    this.cancelPendingPickRequest()
    this.isActivating = false
    this.removeEventHandlers()
    this.stop()
  }

  /** 释放控制器资源 */
  override dispose(): void {
    this.disconnect()
  }

  /**
   * 每帧更新控制器。
   *
   * @param delta - 自上一帧的时间间隔，单位秒
   */
  override update(delta: number): void {
    if (!this.enabled || !this.services) {
      return
    }

    const previous = this.services.rig.getState()
    const camera = this.syncFromRig()
    camera.updateMatrixWorld(true)

    if (this.isActivating) {
      return
    }

    const currentForward = previous.target.clone().sub(previous.position)
    const lookDistance = Math.max(currentForward.length(), 1)
    if (currentForward.lengthSq() < 1e-8) {
      camera.getWorldDirection(currentForward)
    }
    currentForward.normalize()

    let { yaw, pitch } = this.directionToYawPitch(currentForward)
    if (!this.disableRotation) {
      yaw -= this.yawDelta * delta
      pitch = THREE.MathUtils.clamp(pitch - this.pitchDelta * delta, MIN_PITCH, MAX_PITCH)
    }

    const nextForward = this.yawPitchToDirection(yaw, pitch)
    const side = this.yawToSide(yaw)
    const screenUp = side.clone().cross(nextForward).normalize()

    const movement = new THREE.Vector3()
    this.refreshKeyboardMotionProfile(lookDistance)
    this.accumulateKeyboardMovement(nextForward, side, WORLD_UP, movement, delta)

    movement.addScaledVector(side, this.translationDelta.x * delta)
    movement.addScaledVector(nextForward, this.translationDelta.y * delta)
    movement.addScaledVector(screenUp, this.translationDelta.z * delta)
    movement.addScaledVector(WORLD_UP, this.translationWorldDelta.z * delta)

    const nextPosition = previous.position.clone().add(movement)
    const nextTarget = nextPosition.clone().addScaledVector(nextForward, lookDistance)

    this.services.rig.setState({
      ...previous,
      position: nextPosition,
      target: nextTarget,
      up: screenUp,
      mode: CameraMode.Perspective,
    })

    const syncedCamera = this.syncFromRig()
    syncedCamera.updateMatrixWorld(true)

    const attenuation = Math.exp(-FADE_FACTOR * delta)
    this.yawDelta *= attenuation
    this.pitchDelta *= attenuation
    this.translationDelta.multiplyScalar(attenuation)
    this.translationWorldDelta.multiplyScalar(attenuation)
  }

  /** 停止所有运动累积，重置输入状态 */
  stop(): void {
    this.yawDelta = 0
    this.pitchDelta = 0
    this.translationDelta.set(0, 0, 0)
    this.translationWorldDelta.set(0, 0, 0)
    this.keyboardCurrentSpeed = 0
    this.keyState.clear()
    this.activePointerId = null
    this.activeDragButton = null
    this.lastPointerScreen = null
  }

  private syncFromRig(): ReturnType<typeof syncViewportCameraFromRig> {
    if (!this.services) {
      return this.object as ReturnType<typeof syncViewportCameraFromRig>
    }

    const camera = syncViewportCameraFromRig(this.services)
    if (this.object !== camera) {
      this.object = camera
    }

    return camera
  }

  private installEventHandlers(element: HTMLElement): void {
    this.removeEventHandlers()

    this.boundPointerDown = (event: PointerEvent) => {
      if (!this.enabled || this.isActivating || (event.button !== 0 && event.button !== 2)) {
        return
      }

      element.focus()
      this.activePointerId = event.pointerId
      this.activeDragButton = event.button
      this.lastPointerScreen = new THREE.Vector2(event.clientX, event.clientY)
      event.preventDefault()

      if (typeof element.setPointerCapture === 'function') {
        element.setPointerCapture(event.pointerId)
      }
    }

    this.boundPointerMove = (event: PointerEvent) => {
      if (
        !this.enabled ||
        this.isActivating ||
        this.activePointerId !== event.pointerId ||
        !this.lastPointerScreen ||
        this.activeDragButton === null
      ) {
        return
      }

      const current = new THREE.Vector2(event.clientX, event.clientY)
      const delta = current.clone().sub(this.lastPointerScreen)
      this.lastPointerScreen.copy(current)

      if (delta.lengthSq() === 0) {
        return
      }

      const width = Math.max(element.clientWidth, 1)
      const height = Math.max(element.clientHeight, 1)
      const dragX = delta.x / width
      const dragY = delta.y / height

      if (this.activeDragButton === 0) {
        if (this.disableRotation) {
          return
        }

        this.yawDelta += dragX * LEGACY_ROTATION_SPEED
        this.pitchDelta += dragY * LEGACY_ROTATION_SPEED
      } else if (this.activeDragButton === 2) {
        const dragTranslationSpeed = this.movementSpeed * DRAG_TRANSLATION_SPEED_MULTIPLIER
        this.translationDelta.x -= dragX * dragTranslationSpeed * DRAG_TRANSLATION_SCALE
        this.translationDelta.z += dragY * dragTranslationSpeed * DRAG_TRANSLATION_SCALE
      }

      event.preventDefault()
    }

    this.boundPointerUp = (event: PointerEvent) => {
      if (this.activePointerId !== event.pointerId) {
        return
      }

      event.preventDefault()
      this.activePointerId = null
      this.activeDragButton = null
      this.lastPointerScreen = null

      if (typeof element.releasePointerCapture === 'function') {
        element.releasePointerCapture(event.pointerId)
      }
    }

    this.boundWheel = (event: WheelEvent) => {
      if (!this.enabled || this.isActivating) {
        return
      }

      if (event.deltaY < 0) {
        this.movementSpeed /= 0.9
      } else if (event.deltaY > 0) {
        this.movementSpeed *= 0.9
      }

      this.movementSpeed = Math.max(this.movementSpeed, MIN_SPEED)
      event.preventDefault()
    }

    this.boundContextMenu = (event: MouseEvent) => {
      if (!this.enabled) {
        return
      }
      event.preventDefault()
    }

    this.boundKeyDown = (event: KeyboardEvent) => {
      if (!this.enabled || this.isActivating) {
        return
      }
      this.keyState.add(event.code)
    }

    this.boundKeyUp = (event: KeyboardEvent) => {
      this.keyState.delete(event.code)
    }

    this.boundDoubleClick = (event: MouseEvent) => {
      if (!this.enabled || event.defaultPrevented) {
        return
      }

      if (shouldUseAsyncPicking(this.services)) {
        event.preventDefault()
        this.scheduleDoubleClickPick(event)
      } else {
        const pivot = this.pickPoint(event)
        if (!pivot) return
        void this.focusOnPoint(pivot)
        event.preventDefault()
      }
    }

    element.addEventListener('pointerdown', this.boundPointerDown as EventListener)
    element.addEventListener('pointermove', this.boundPointerMove as EventListener)
    element.addEventListener('pointerup', this.boundPointerUp as EventListener)
    element.addEventListener('pointercancel', this.boundPointerUp as EventListener)
    element.addEventListener('wheel', this.boundWheel as EventListener, { passive: false })
    element.addEventListener('contextmenu', this.boundContextMenu as EventListener)
    element.addEventListener('dblclick', this.boundDoubleClick as EventListener)
    window.addEventListener('keydown', this.boundKeyDown)
    window.addEventListener('keyup', this.boundKeyUp)
  }

  private removeEventHandlers(): void {
    if (!this.domElement) {
      return
    }

    if (this.boundPointerDown) {
      this.domElement.removeEventListener('pointerdown', this.boundPointerDown as EventListener)
      this.boundPointerDown = null
    }

    if (this.boundPointerMove) {
      this.domElement.removeEventListener('pointermove', this.boundPointerMove as EventListener)
      this.boundPointerMove = null
    }

    if (this.boundPointerUp) {
      this.domElement.removeEventListener('pointerup', this.boundPointerUp as EventListener)
      this.domElement.removeEventListener('pointercancel', this.boundPointerUp as EventListener)
      this.boundPointerUp = null
    }

    if (this.boundWheel) {
      this.domElement.removeEventListener('wheel', this.boundWheel as EventListener)
      this.boundWheel = null
    }

    if (this.boundContextMenu) {
      this.domElement.removeEventListener('contextmenu', this.boundContextMenu as EventListener)
      this.boundContextMenu = null
    }

    if (this.boundDoubleClick) {
      this.domElement.removeEventListener('dblclick', this.boundDoubleClick as EventListener)
      this.boundDoubleClick = null
    }

    if (this.boundKeyDown) {
      window.removeEventListener('keydown', this.boundKeyDown)
      this.boundKeyDown = null
    }

    if (this.boundKeyUp) {
      window.removeEventListener('keyup', this.boundKeyUp)
      this.boundKeyUp = null
    }

    this.stop()
  }

  private accumulateKeyboardMovement(
    forward: THREE.Vector3,
    side: THREE.Vector3,
    worldUp: THREE.Vector3,
    out: THREE.Vector3,
    delta: number
  ): void {
    const moveForward = this.keyState.has('KeyW') || this.keyState.has('ArrowUp')
    const moveBackward = this.keyState.has('KeyS') || this.keyState.has('ArrowDown')
    const moveLeft = this.keyState.has('KeyA') || this.keyState.has('ArrowLeft')
    const moveRight = this.keyState.has('KeyD') || this.keyState.has('ArrowRight')
    const moveUp = this.keyState.has('KeyQ') || this.keyState.has('PageUp')
    const moveDown = this.keyState.has('KeyE') || this.keyState.has('PageDown')

    if (moveForward === moveBackward && moveLeft === moveRight && moveUp === moveDown) {
      this.keyboardCurrentSpeed = 0
      return
    }

    const safeDelta = Number.isFinite(delta) && delta > 0 ? delta : 0
    const moveSpeed = this.advanceKeyboardSpeed(safeDelta)

    const projectedForward = forward.clone()
    if (this.lockElevation) {
      projectedForward.z = 0
      if (projectedForward.lengthSq() > 1e-8) {
        projectedForward.normalize()
      } else {
        projectedForward.copy(forward)
      }
    }

    if (moveForward !== moveBackward) {
      out.addScaledVector(projectedForward, moveForward ? moveSpeed : -moveSpeed)
    }

    if (moveLeft !== moveRight) {
      out.addScaledVector(side, moveRight ? moveSpeed : -moveSpeed)
    }

    if (moveUp !== moveDown) {
      out.addScaledVector(worldUp, moveUp ? moveSpeed : -moveSpeed)
    }
  }

  private refreshKeyboardMotionProfile(distance: number): void {
    const nextMaxSpeed = resolveNavigationScale({
      cameraTargetDistance: distance,
      sceneBounds: null,
      absoluteMinimum: Math.max(this.movementSpeed, MIN_SPEED, MIN_KEYBOARD_MAX_SPEED),
    })

    if (Math.abs(nextMaxSpeed - this.keyboardMaxSpeed) <= 1e-6) {
      return
    }

    this.keyboardMaxSpeed = nextMaxSpeed
    this.keyboardCurrentSpeed = Math.min(this.keyboardCurrentSpeed, nextMaxSpeed)
  }

  private advanceKeyboardSpeed(delta: number): number {
    const acceleration = this.keyboardMaxSpeed / KEYBOARD_ACCELERATION_DURATION
    this.keyboardCurrentSpeed = Math.min(
      this.keyboardMaxSpeed,
      this.keyboardCurrentSpeed + acceleration * delta
    )
    return this.keyboardCurrentSpeed * delta
  }

  private directionToYawPitch(direction: THREE.Vector3): { yaw: number; pitch: number } {
    if (Math.abs(direction.x) < 1e-8 && Math.abs(direction.y) < 1e-8) {
      return {
        yaw: 0,
        pitch: (Math.PI / 2) * Math.sign(direction.z || 1),
      }
    }

    return {
      yaw: Math.atan2(direction.y, direction.x) - Math.PI / 2,
      pitch: Math.atan2(
        direction.z,
        Math.sqrt(direction.x * direction.x + direction.y * direction.y)
      ),
    }
  }

  private yawPitchToDirection(yaw: number, pitch: number): THREE.Vector3 {
    return new THREE.Vector3(0, 1, 0)
      .applyAxisAngle(new THREE.Vector3(1, 0, 0), pitch)
      .applyAxisAngle(WORLD_UP, yaw)
      .normalize()
  }

  private yawToSide(yaw: number): THREE.Vector3 {
    return new THREE.Vector3(1, 0, 0).applyAxisAngle(WORLD_UP, yaw).normalize()
  }

  private pickPoint(event: MouseEvent): THREE.Vector3 | null {
    if (!this.services?.surfaceQuery || !this.domElement) {
      return null
    }

    const screen = new THREE.Vector2(event.clientX, event.clientY)
    const camera = this.syncFromRig()
    const result = this.services.surfaceQuery.queryAtScreen(screen, camera, {
      kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
      interactionType: 'click',
    })

    return result?.point.clone() ?? null
  }

  private scheduleDoubleClickPick(event: MouseEvent): void {
    if (!this.services?.surfaceQuery?.queryAtScreenAsync) return
    const version = ++this.pickRequestVersion
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    const camera = this.syncFromRig()
    void this.services.surfaceQuery
      .queryAtScreenAsync(new THREE.Vector2(event.clientX, event.clientY), camera, {
        kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
        interactionType: 'click',
        signal: controller.signal,
      })
      .then((hit) => {
        if (
          hit &&
          version === this.pickRequestVersion &&
          this.enabled &&
          isCurrentViewportCamera(this.services!, camera)
        ) {
          void this.focusOnPoint(hit.point)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
        }
      })
  }

  private cancelPendingPickRequest(): void {
    this.pickRequestVersion += 1
    this.pickAbortController?.abort()
    this.pickAbortController = null
  }

  private async focusOnPoint(point: THREE.Vector3): Promise<void> {
    if (!this.services) {
      return
    }

    const previous = this.services.rig.getState()
    const direction = previous.target.clone().sub(previous.position).normalize()
    const distanceToPoint = Math.max(previous.position.distanceTo(point), 1e-6)
    const focusDistance = THREE.MathUtils.clamp(
      distanceToPoint * DOUBLE_CLICK_FOCUS_RATIO,
      DOUBLE_CLICK_MIN_DISTANCE,
      distanceToPoint
    )
    const nextPosition = point.clone().addScaledVector(direction, -focusDistance)

    await this.services.rig.animateTo(
      {
        position: nextPosition,
        target: point.clone(),
        up: previous.up.clone(),
        mode: CameraMode.Perspective,
      },
      DOUBLE_CLICK_DURATION_MS
    )
  }

  private async beginActivationTransition(): Promise<void> {
    if (!this.services) {
      return
    }

    const state = this.services.rig.getState()
    const forward = state.target.clone().sub(state.position)
    if (forward.lengthSq() < 1e-8) {
      this.isActivating = false
      return
    }

    const desiredUp = computeRollFreeUp(forward)
    if (desiredUp.angleTo(state.up) <= UP_ALIGNMENT_EPSILON) {
      this.isActivating = false
      return
    }

    const token = ++this.activationToken
    this.isActivating = true
    this.stop()

    await this.services.rig.animateTo(
      {
        up: desiredUp,
        mode: CameraMode.Perspective,
      },
      FIRST_PERSON_ACTIVATION_TRANSITION_MS
    )

    if (this.activationToken !== token) {
      return
    }

    this.isActivating = false
  }
}

export default LegacyFirstPersonController
